import React, { useState, useEffect } from 'react';
import { Project } from '../../types/project';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ProjectForm } from './ProjectForm';
import { Modal } from '../ui/modal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PencilIcon, 
  TrashIcon, 
  PlusIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowsUpDownIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { useProjects } from '../../contexts/ProjectContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { DndKitWrapper, SortableItem } from '../ui/dnd-kit-wrapper';
import { GripVertical } from 'lucide-react';

interface ProjectListProps {}

export const ProjectList: React.FC<ProjectListProps> = () => {
  const context = useProjects();
  const { projects, tasks, deleteProject, loading } = context;
  const error = (context as any).error;
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'date'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    const filtered = projects
      .filter(project => 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        if (sortBy === 'name') {
          return sortOrder === 'asc' 
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }
        return sortOrder === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });

    setFilteredProjects(filtered);
  }, [projects, searchQuery, sortBy, sortOrder]);

  const visibleProjects = showAllProjects ? filteredProjects : filteredProjects.slice(0, 6);
  const hasMoreProjects = filteredProjects.length > 6;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
          </div>
          <p className="text-lg font-medium text-red-500">Failed to load projects</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteProject(projectToDelete.id);
      setProjectToDelete(null);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsDeleting(false);
    }
  };


  const handleDragEnd = (reorderedItems: any[]) => {
    try {
      setFilteredProjects(reorderedItems);
      // Persist the new order to localStorage
      const orderMap = reorderedItems.map((item, index) => ({ id: item.id, order: index }));
      localStorage.setItem('pomomax-project-order', JSON.stringify(orderMap));
      toast.success('Project order updated');
    } catch (error) {
      console.error('Error reordering projects:', error);
      toast.error('Failed to reorder projects');
    }
  };

  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16 space-y-6">
          <div className="flex justify-center">
            <div className="bg-primary/10 rounded-full p-4">
              <RocketLaunchIcon className="h-12 w-12 text-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold">No projects found</h3>
            <p className="text-muted-foreground max-w-sm mx-auto">
              Organize your tasks and track your progress by creating projects. Get started by creating your first one!
            </p>
          </div>
          <Button onClick={() => setShowProjectForm(true)} size="lg" className="gap-2">
            <PlusIcon className="h-5 w-5" />
            Create Project
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-medium">Projects</h2>
            <span className="text-sm text-muted-foreground">
              ({projects.length} total)
            </span>
          </div>
          <Button
            onClick={() => {
              setShowProjectForm(true);
            }}
            className="w-full sm:w-auto gap-2"
            aria-label="Add Project"
          >
            <PlusIcon className="h-4 w-4" />
            New Project
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="relative flex-1 sm:w-64">
            <Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          </div>

          <div className="flex gap-2 sm:flex-none">
            <div className="relative inline-block">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex-1 sm:flex-none gap-2"
                aria-label="Sort by"
              >
                <ArrowsUpDownIcon className="h-4 w-4" />
                Sort by {sortBy === 'name' ? 'Name' : 'Date'}
              </Button>
              {showSortMenu && (
                <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-md shadow-lg z-10 min-w-[120px]">
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-accent text-sm"
                    onClick={() => {
                      setSortBy('name');
                      setSortOrder('asc');
                      setShowSortMenu(false);
                    }}
                  >
                    Name
                  </button>
                  <button
                    className="block w-full text-left px-4 py-2 hover:bg-accent text-sm"
                    onClick={() => {
                      setSortBy('date');
                      setSortOrder('desc');
                      setShowSortMenu(false);
                    }}
                  >
                    Date
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <DndKitWrapper
          items={filteredProjects}
          onDragEnd={handleDragEnd}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-0 sm:px-4"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.length > 0 ? (
              visibleProjects.map((project) => (
                <SortableItem
                  key={project.id}
                  id={project.id}
                  className="group relative bg-card rounded-xl shadow-sm border border-border overflow-hidden"
                  data-testid={`project-item-${project.id}`}
                >
                  <div className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-1.5 rounded-md hover:bg-muted" data-testid={`drag-handle-${project.id}`}>
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>

                  <div className="p-4 sm:p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg shadow-sm flex items-center justify-center"
                        style={{
                          backgroundColor: project.color,
                          boxShadow: `0 0 12px ${project.color}40`
                        }}
                        data-testid={`project-color-${project.id}`}
                      >
                        <RocketLaunchIcon className="h-5 w-5 text-white/90" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{project.name}</h3>
                      </div>
                    </div>

                    {project.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-2">
                      <div className="text-xs text-muted-foreground">
                        Created {project.createdAt ? (() => {
                          try {
                            return format(new Date(project.createdAt), 'M/d/yyyy');
                          } catch (error) {
                            return 'Unknown date';
                          }
                        })() : 'Unknown date'}
                      </div>
                      
                      <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingProject(project);
                                  setShowProjectForm(true);
                                }}
                                className="h-8 w-8 p-0 hover:bg-primary/10 hover:text-primary"
                                aria-label={`Edit ${project.name}`}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit project</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>

                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setProjectToDelete(project);
                                }}
                                className="h-8 w-8 p-0 hover:bg-red-500/10 hover:text-red-500"
                                aria-label={`Delete ${project.name}`}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete project</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
                      {(() => {
                        const projectTasks = tasks.filter(t => t.projectId === project.id);
                        const completedTasks = projectTasks.filter(t => t.completed).length;
                        const totalTasks = projectTasks.length;
                        const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                        return (
                          <div
                            className="h-full transition-all duration-300"
                            style={{
                              backgroundColor: project.color,
                              width: `${progress}%`
                            }}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </SortableItem>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="text-center py-12 col-span-full"
              >
                {searchQuery ? (
                  <div className="space-y-4">
                    <div className="flex justify-center">
                      <div className="bg-muted/50 rounded-full p-3">
                        <MagnifyingGlassIcon className="h-6 w-6 text-muted-foreground" />
                      </div>
                    </div>
                    <div>
                      <p className="text-lg font-medium">No matching projects found</p>
                      <p className="text-muted-foreground mt-1">
                        Try adjusting your search term or filters
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setSearchQuery('')}
                      className="mt-4"
                      aria-label="Clear search"
                    >
                      Clear Search
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-lg font-medium">No projects yet</p>
                    <p className="text-muted-foreground">
                      Create your first project to get started
                    </p>
                    <Button onClick={() => setShowProjectForm(true)} className="mt-4">
                      Create Project
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </DndKitWrapper>

        {hasMoreProjects && !showAllProjects && (
          <Button
            variant="outline"
            onClick={() => setShowAllProjects(true)}
            className="w-full gap-2"
          >
            <ArrowsUpDownIcon className="h-4 w-4" />
            Show More
          </Button>
        )}
      </div>

      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => {
          setShowProjectForm(false);
          setEditingProject(null);
        }}
        initialData={editingProject}
      />

      <Modal
        isOpen={!!projectToDelete}
        onClose={() => !isDeleting && setProjectToDelete(null)}
        title="Delete Project"
        description="Permanently delete this project and all associated data"
      >
        <div className="p-6">
          {projectToDelete && (
            <div className="mb-6 bg-red-500/5 rounded-xl p-4 border border-red-500/10">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-lg"
                  style={{ backgroundColor: projectToDelete.color }}
                />
                <div>
                  <h3 className="font-medium text-lg">{projectToDelete.name}</h3>
                  {projectToDelete.description && (
                    <p className="text-sm text-muted-foreground">
                      {projectToDelete.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3 text-red-500">
              <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5" />
              </div>
              <h3 className="font-semibold">
                Are you absolutely sure?
              </h3>
            </div>

            <div className="ml-[3.25rem] space-y-3">
              <p className="text-muted-foreground">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                  All project data and settings
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                  Associated tasks and their progress
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                  Historical pomodoro sessions
                </li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setProjectToDelete(null)}
              disabled={isDeleting}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="min-w-[100px] gap-2"
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <TrashIcon className="h-4 w-4" />
                  <span>Delete</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};